import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiPlus } from "react-icons/fi";
import { api } from "../../services/api";

import { ItemPagination } from "../../components/ItemPagination";
import { PageTitle } from "../../components/PageTitle";
import { SelectSearchFilter } from "../../components/SelectSearchFilter";
import { EmptyState } from "../../components/EmptyState";
import { CanAccess } from "../../components/CanAccess";
import { useAuth } from "../../contexts/AuthContext";

interface Sector {
  id: string;
  name: string;
  status: string;
  costumer: Costumer;
}

interface Costumer {
  id: string;
  name: string;
}

export function Sectors() {
  const { hasPermission } = useAuth();
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [costumer_id, setCostumerId] = useState("");
  const [changeStatus, setChangeStatus] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const LIMIT = 10;

  // Função para carregar a primeira página ou reiniciar os filtros
  const loadSectors = useCallback(() => {
    setLoading(true);
    setPage(1);
    api
      .get("/sectors", {
        params: {
          costumer_id,
          page: 1,
          limit: LIMIT,
        },
      })
      .then(response => {
        const result = response.data.data || [];
        setSectors(result);
        setHasMore(result.length >= LIMIT);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [costumer_id]);

  // Função para carregar mais páginas ao fazer scroll
  const loadMoreSectors = useCallback(() => {
    if (loading || !hasMore) return;

    setLoading(true);
    api
      .get("/sectors", {
        params: {
          costumer_id,
          page,
          limit: LIMIT,
        },
      })
      .then(response => {
        const result = response.data.data || [];
        setSectors(prev => [...prev, ...result]);
        setHasMore(result.length >= LIMIT);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [costumer_id, page, loading, hasMore]);

  // Carregar dados ao alterar filtros
  useEffect(() => {
    loadSectors();
  }, [costumer_id, changeStatus, loadSectors]);

  // Carregar mais dados ao alterar a página
  useEffect(() => {
    if (page > 1) {
      loadMoreSectors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Função de scroll para carregar mais dados
  const handleScroll = useCallback(() => {
    if (loading || !hasMore) return;

    const mainElement = document.querySelector("main");
    if (!mainElement) return;

    const scrollPosition = mainElement.scrollTop + mainElement.clientHeight;
    const bottomPosition = mainElement.scrollHeight;

    if (scrollPosition >= bottomPosition - 100) {
      setPage(prev => prev + 1);
    }
  }, [loading, hasMore]);

  // Adicionar e remover evento de scroll
  useEffect(() => {
    const mainElement = document.querySelector("main");
    if (!mainElement) return undefined;

    mainElement.addEventListener("scroll", handleScroll);
    return () => mainElement.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  async function handleChangeStatus(id: string, newStatus: string) {
    api.patch(`sectors/status/${id}/${newStatus === "A" ? "I" : "A"}`);
    setTimeout(() => {
      setChangeStatus(changeStatus + 1);
    }, 1000);
  }

  return (
    <div className="container mx-auto">
      <PageTitle title="Setores" backButton backPath="/home" />
      <div className="flex items-start justify-between mb-6">
        <CanAccess permission="sectors:create" allowAdmin>
          <Link
            to="/sectors/new"
            className="group bg-[#0085BD] text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:bg-[#006a96] transition-all duration-200 hover:shadow-xl"
          >
            <FiPlus size={24} className="transition-transform duration-200 group-hover:rotate-90" />
          </Link>
        </CanAccess>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6">
        {/* Header do card de filtros */}
        <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4 rounded-t-2xl">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
            </svg>
            Filtros
          </h3>
        </div>
        <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-700">
              Cliente
            </span>
            <SelectSearchFilter
              placeholder="Buscar cliente..."
              value={costumer_id}
              onChange={setCostumerId}
            />
          </div>
        </div>
        </div>
      </div>

      <div className="space-y-2">
        {sectors.map(({ id, name, costumer, status }: Sector) => (
          <ItemPagination
            key={id}
            title={name}
            description={costumer.name}
            id={id}
            status={status}
            hasDelete
            canEdit={hasPermission("sectors:update")}
            canDelete={hasPermission("sectors:delete")}
            handleChangeStatus={() => handleChangeStatus(id, status)}
          />
        ))}

        {/* Loading inline para scroll infinito */}
        {loading && (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0085BD]" />
          </div>
        )}

        {!loading && sectors.length === 0 && <EmptyState type="sectors" />}
      </div>
    </div>
  );
}
